import { useCallback, useRef, useState } from 'react';

const MAX_AUDIO_DURATION_SECONDS = 300;

export interface RecordedAudio {
  readonly file: File;
  readonly durationSeconds: number;
}

function pickAudioMimeType(): string {
  try {
    if (MediaRecorder.isTypeSupported('audio/mp4')) {
      return 'audio/mp4';
    }
    if (MediaRecorder.isTypeSupported('audio/webm')) {
      return 'audio/webm';
    }
  } catch {
    return '';
  }
  return '';
}

function asAllowedAudioType(
  mimeType: string,
): 'audio/webm' | 'audio/mp4' | 'audio/mpeg' | 'audio/aac' | 'audio/m4a' {
  if (mimeType.startsWith('audio/webm')) return 'audio/webm';
  if (mimeType.startsWith('audio/mp4')) return 'audio/mp4';
  if (mimeType.startsWith('audio/mpeg')) return 'audio/mpeg';
  if (mimeType.startsWith('audio/aac')) return 'audio/aac';
  if (mimeType.startsWith('audio/m4a')) return 'audio/m4a';
  return 'audio/mp4';
}

/** MediaRecorder voice note, capped at 5 minutes. */
export function useAudioRecorder(): {
  readonly recording: boolean;
  readonly paused: boolean;
  readonly elapsedSeconds: number;
  readonly start: () => Promise<void>;
  readonly stop: () => Promise<RecordedAudio | null>;
  readonly pause: () => void;
  readonly resume: () => void;
  readonly cancel: () => Promise<void>;
} {
  const [recording, setRecording] = useState(false);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const startedAtRef = useRef<number | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const capTimerRef = useRef<number | null>(null);
  const tickTimerRef = useRef<number | null>(null);
  const stopResolverRef = useRef<((value: RecordedAudio | null) => void) | null>(null);
  const cancelledRef = useRef(false);
  const pausedMsRef = useRef(0);
  const pauseStartedRef = useRef<number | null>(null);
  const [paused, setPaused] = useState(false);

  const readElapsedMs = (): number => {
    const started = startedAtRef.current;
    if (started === null) return 0;
    const openPause = pauseStartedRef.current === null ? 0 : Date.now() - pauseStartedRef.current;
    return Math.max(0, Date.now() - started - pausedMsRef.current - openPause);
  };

  const cleanupStream = (): void => {
    streamRef.current?.getTracks().forEach((track) => {
      track.stop();
    });
    streamRef.current = null;
    if (capTimerRef.current !== null) {
      window.clearTimeout(capTimerRef.current);
      capTimerRef.current = null;
    }
    if (tickTimerRef.current !== null) {
      window.clearInterval(tickTimerRef.current);
      tickTimerRef.current = null;
    }
  };

  const start = useCallback(async (): Promise<void> => {
    const mimeType = pickAudioMimeType();
    if (mimeType.length === 0) {
      throw new Error('Este navegador não grava áudio no formato permitido.');
    }
    let stream: MediaStream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch {
      throw new Error('Este navegador não grava áudio.');
    }
    streamRef.current = stream;
    cancelledRef.current = false;
    const recorder = new MediaRecorder(stream, { mimeType });
    chunksRef.current = [];
    recorder.ondataavailable = (event) => {
      if (event.data.size > 0) {
        chunksRef.current.push(event.data);
      }
    };
    recorder.onstop = () => {
      const elapsed = Math.round(readElapsedMs() / 1000);
      startedAtRef.current = null;
      pauseStartedRef.current = null;
      pausedMsRef.current = 0;
      setPaused(false);
      const durationSeconds = Math.min(Math.max(elapsed, 1), MAX_AUDIO_DURATION_SECONDS);
      const type = asAllowedAudioType(recorder.mimeType || mimeType);
      const blob = new Blob(chunksRef.current, { type });
      chunksRef.current = [];
      cleanupStream();
      mediaRecorderRef.current = null;
      setRecording(false);
      setElapsedSeconds(0);
      const resolve = stopResolverRef.current;
      stopResolverRef.current = null;
      const cancelled = cancelledRef.current;
      cancelledRef.current = false;
      if (cancelled || blob.size <= 0) {
        resolve?.(null);
        return;
      }
      const file = new File([blob], `chat-audio.${type === 'audio/webm' ? 'webm' : 'm4a'}`, {
        type,
      });
      resolve?.({ file, durationSeconds });
    };
    mediaRecorderRef.current = recorder;
    startedAtRef.current = Date.now();
    pausedMsRef.current = 0;
    pauseStartedRef.current = null;
    setPaused(false);
    setElapsedSeconds(0);
    recorder.start();
    setRecording(true);
    tickTimerRef.current = window.setInterval(() => {
      if (startedAtRef.current === null) return;
      setElapsedSeconds(Math.floor(readElapsedMs() / 1000));
    }, 250);
    capTimerRef.current = window.setTimeout(() => {
      if (recorder.state !== 'inactive') {
        recorder.stop();
      }
    }, MAX_AUDIO_DURATION_SECONDS * 1000);
  }, []);

  const pause = useCallback((): void => {
    const recorder = mediaRecorderRef.current;
    if (recorder?.state !== 'recording') return;
    recorder.pause();
    pauseStartedRef.current = Date.now();
    setPaused(true);
  }, []);

  const resume = useCallback((): void => {
    const recorder = mediaRecorderRef.current;
    if (recorder?.state !== 'paused') return;
    if (pauseStartedRef.current !== null) {
      pausedMsRef.current += Date.now() - pauseStartedRef.current;
      pauseStartedRef.current = null;
    }
    recorder.resume();
    setPaused(false);
  }, []);

  const stop = useCallback(async (): Promise<RecordedAudio | null> => {
    const recorder = mediaRecorderRef.current;
    if (recorder === null || recorder.state === 'inactive') {
      return null;
    }
    return new Promise((resolve) => {
      stopResolverRef.current = resolve;
      recorder.stop();
    });
  }, []);

  const cancel = useCallback(async (): Promise<void> => {
    cancelledRef.current = true;
    await stop();
  }, [stop]);

  return { recording, paused, elapsedSeconds, start, stop, pause, resume, cancel };
}
